import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@material-ui/core";
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: 0,
    minHeight: 400,
  },
  searchField: {
    margin: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  productList: {
    maxHeight: 350,
    overflow: "auto",
  },
  productItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  productAvatar: {
    width: 50,
    height: 50,
    borderRadius: theme.spacing(1),
  },
  productInfo: {
    flex: 1,
  },
  productName: {
    fontWeight: 500,
  },
  productCategory: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  featuredChip: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    marginLeft: theme.spacing(1),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
  },
}));

const FeaturedProductsManager = ({ open, onClose, onUpdate }) => {
  const classes = useStyles();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get("/catalog/products", {
        params: {
          pageNumber: 1,
          searchParam: searchTerm,
        },
      });
      setProducts(response.data.products || []);
    } catch (err) {
      console.error("Error loading products:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    loadProducts();
  };

  const handleToggleFeatured = async (productId, currentFeatured) => {
    try {
      setUpdating(true);
      const action = currentFeatured ? "unfeature" : "feature";
      
      await api.post("/catalog/featured", {
        productIds: [productId],
        action,
      });

      // Update local state
      setProducts(prevProducts =>
        prevProducts.map(product =>
          product.id === productId
            ? { ...product, featured: !currentFeatured }
            : product
        )
      );

      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error("Error updating featured status:", err);
      toastError(err);
    } finally {
      setUpdating(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price / 100);
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="featured-products-dialog-title"
    >
      <DialogTitle id="featured-products-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Gerenciar Produtos em Destaque
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <TextField
          className={classes.searchField}
          fullWidth
          placeholder="Buscar produtos..."
          value={searchTerm}
          onChange={handleSearch}
          onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body1">
              {searchTerm ? "Nenhum produto encontrado" : "Nenhum produto disponível"}
            </Typography>
            <Typography variant="body2">
              {searchTerm ? "Tente ajustar os termos de busca" : "Crie produtos para gerenciar destaques"}
            </Typography>
          </div>
        ) : (
          <List className={classes.productList}>
            {filteredProducts.map((product) => (
              <ListItem key={product.id} className={classes.productItem}>
                <ListItemAvatar>
                  <Avatar
                    src={product.imageUrl}
                    className={classes.productAvatar}
                    variant="rounded"
                  >
                    {product.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  className={classes.productInfo}
                  primary={
                    <Box display="flex" alignItems="center">
                      <Typography className={classes.productName}>
                        {product.name}
                      </Typography>
                      {product.featured && (
                        <Chip
                          label="Destaque"
                          size="small"
                          className={classes.featuredChip}
                          icon={<StarIcon />}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography className={classes.productCategory}>
                        {product.category} • {formatPrice(product.price)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {product.viewCount || 0} visualizações • {product.salesCount || 0} vendas
                      </Typography>
                    </Box>
                  }
                />
                
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleToggleFeatured(product.id, product.featured)}
                    disabled={updating}
                    color={product.featured ? "primary" : "default"}
                  >
                    {product.featured ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeaturedProductsManager;